if (!customElements.get('store-draft-checkout')) {
  customElements.define(
    'store-draft-checkout',
    class StoreDraftCheckout extends HTMLElement {
      constructor() {
        super();
        this.addressStorageKey = 'lumenora:store-draft-checkout:address:v1';
        this.recoveryStorageKey = 'lumenora:store-draft-checkout:recovery:v1';
        this.cart = null;
        this.quoteToken = null;
        this.idempotencyKey = null;
        this.currency = this.dataset.currency || 'USD';
      }

      connectedCallback() {
        this.form = this.querySelector('[data-checkout-form]');
        this.layout = this.querySelector('[data-checkout-layout]');
        this.loadingState = this.querySelector('[data-loading-state]');
        this.emptyState = this.querySelector('[data-empty-state]');
        this.shippingSection = this.querySelector('[data-shipping-section]');
        this.shippingOptions = this.querySelector('[data-shipping-options]');
        this.message = this.querySelector('[data-message]');
        this.quoteButton = this.querySelector('[data-quote-button]');
        this.createButton = this.querySelector('[data-create-button]');
        this.detailsEditor = this.querySelector('[data-details-editor]');
        this.detailsSummary = this.querySelector('[data-details-summary]');
        this.detailsStatus = this.querySelector('[data-details-status]');
        this.detailsStatusLabel = this.querySelector('[data-details-status-label]');

        this.form.addEventListener('submit', (event) => this.handleQuote(event));
        this.createButton.addEventListener('click', () => this.handleCreate());
        this.querySelector('[data-edit-details]').addEventListener('click', () => this.showDetailsEditor());
        this.form.addEventListener('input', (event) => {
          if (
            event.target.name !== 'rememberAddress' &&
            event.target.name !== 'shippingOption' &&
            this.quoteToken
          ) {
            this.invalidateQuote();
          }
          if (
            event.target.name !== 'rememberAddress' &&
            event.target.name !== 'shippingOption' &&
            this.isRememberAddressEnabled()
          ) {
            this.saveAddress();
          }
        });
        this.form.addEventListener('change', (event) => {
          if (event.target.name === 'rememberAddress') {
            if (event.target.checked) {
              this.saveAddress();
            } else {
              this.clearSavedAddress();
            }
            return;
          }
          if (event.target.name !== 'shippingOption' && event.target.name !== 'rememberAddress' && this.quoteToken) {
            this.invalidateQuote();
          }
          if (event.target.name === 'shippingOption') {
            this.updateSelectedShippingTotal();
          }
          if (event.target.name !== 'shippingOption' && this.isRememberAddressEnabled()) {
            this.saveAddress();
          }
        });

        this.restoreSavedAddress();
        if (this.hasCompleteSavedAddress()) {
          this.showDetailsSummary();
        } else {
          this.showDetailsEditor();
        }
        this.loadCart();
      }

      async loadCart() {
        try {
          const response = await fetch(`${this.dataset.rootUrl}cart.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error('Unable to load your cart.');

          this.cart = await response.json();
          this.currency = this.cart.currency || this.currency;

          if (!this.cart.items?.length) {
            this.loadingState.hidden = true;
            this.showEmptyState();
            return;
          }

          this.renderCart();
          this.loadingState.hidden = true;
          this.layout.hidden = false;
          if (this.hasCompleteSavedAddress()) {
            await this.handleQuote(null, { automatic: true });
          }
        } catch (error) {
          this.loadingState.hidden = true;
          this.layout.hidden = false;
          this.showMessage(error.message);
        }
      }

      renderCart() {
        const container = this.querySelector('[data-cart-items]');
        const fragment = document.createDocumentFragment();
        container.replaceChildren();

        this.cart.items.forEach((item) => {
          const row = document.createElement('div');
          row.className = 'store-draft-checkout__item';

          const imageWrap = document.createElement('div');
          imageWrap.className = 'store-draft-checkout__item-image';
          if (item.image) {
            const image = document.createElement('img');
            image.src = this.resizeImage(item.image, '160x');
            image.alt = item.product_title || '';
            image.loading = 'lazy';
            image.width = 80;
            image.height = 80;
            imageWrap.append(image);
          }

          const quantity = document.createElement('span');
          quantity.className = 'store-draft-checkout__item-quantity';
          quantity.textContent = item.quantity;
          imageWrap.append(quantity);

          const details = document.createElement('div');
          const title = document.createElement('p');
          title.className = 'store-draft-checkout__item-title';
          title.textContent = item.product_title;
          details.append(title);

          if (item.variant_title && item.variant_title !== 'Default Title') {
            const variant = document.createElement('p');
            variant.className = 'store-draft-checkout__item-variant';
            variant.textContent = item.variant_title;
            details.append(variant);
          }

          const price = document.createElement('span');
          price.className = 'store-draft-checkout__item-price';
          price.textContent = this.formatMoney(item.final_line_price / 100);

          row.append(imageWrap, details, price);
          fragment.append(row);
        });

        container.append(fragment);
        this.querySelector('[data-item-count]').textContent = `${this.cart.item_count} ${
          this.cart.item_count === 1 ? 'item' : 'items'
        }`;
        this.querySelector('[data-subtotal]').textContent = this.formatMoney(this.cart.total_price / 100);
      }

      async handleQuote(event, requestOptions = {}) {
        event?.preventDefault();
        this.hideMessage();

        if (!requestOptions.automatic && !this.validateShippingAddress()) return;
        if (!this.cart?.items?.length) {
          this.showMessage('Your cart is empty.');
          return;
        }

        const address = this.getAddress();
        const payload = {
          cartItems: this.cart.items.map((item) => ({
            shopifyProductVariantId: String(item.variant_id),
            quantity: item.quantity,
          })),
          shippingAddress: address,
        };

        this.setLoading(this.quoteButton, true);
        this.setDetailsStatus('Getting shipping options');
        try {
          const result = await this.postJson(this.dataset.quotePath, payload);
          const shippingOptions = result.shippingOptions;

          if (!Array.isArray(shippingOptions) || shippingOptions.length === 0) {
            throw new Error('No shipping methods are available for this address.');
          }
          if (!result.quoteToken) {
            throw new Error('The shipping quote is missing its confirmation token.');
          }

          this.quoteToken = result.quoteToken;
          this.idempotencyKey = this.createUuid();
          this.renderShippingOptions(shippingOptions, result.currency || this.currency);
          this.shippingSection.hidden = false;
          this.showDetailsSummary();
          if (!requestOptions.automatic) {
            this.shippingSection.scrollIntoView({
              behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
              block: 'start',
            });
          }
        } catch (error) {
          this.invalidateQuote();
          this.showMessage(error.message || 'Unable to get shipping options. Please try again.');
        } finally {
          this.setDetailsStatus('');
          this.setLoading(this.quoteButton, false);
        }
      }

      renderShippingOptions(options, currency) {
        const fragment = document.createDocumentFragment();
        this.shippingOptions.replaceChildren();

        options.forEach((option, index) => {
          const label = document.createElement('label');
          label.className = 'store-draft-checkout__shipping-option';

          const radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = 'shippingOption';
          radio.value = String(option.logisticsChannelId);
          radio.dataset.amount = String(option.shippingAmount);
          radio.dataset.currency = currency;
          radio.required = true;
          radio.checked = index === 0;

          const details = document.createElement('span');
          const name = document.createElement('strong');
          name.className = 'store-draft-checkout__shipping-name';
          name.textContent = option.displayName || 'Shipping';
          details.append(name);

          if (option.estimatedDeliveryDays) {
            const eta = document.createElement('span');
            eta.className = 'store-draft-checkout__shipping-eta';
            eta.textContent = `Estimated ${option.estimatedDeliveryDays} business days`;
            details.append(eta);
          }

          const price = document.createElement('strong');
          price.className = 'store-draft-checkout__shipping-price';
          price.textContent = this.formatMoney(Number(option.shippingAmount), currency);

          label.append(radio, details, price);
          fragment.append(label);
        });

        this.shippingOptions.append(fragment);
        this.updateSelectedShippingTotal();
      }

      async handleCreate() {
        this.hideMessage();
        if (!this.form.checkValidity()) {
          if (this.detailsEditor.querySelector(':invalid')) this.showDetailsEditor();
          this.form.reportValidity();
          return;
        }
        const selected = this.form.querySelector('input[name="shippingOption"]:checked');

        if (!this.quoteToken || !selected) {
          this.showMessage('Select a current shipping quote before continuing.');
          return;
        }

        const formData = new FormData(this.form);
        const payload = {
          quoteToken: this.quoteToken,
          selectedLogisticsChannelId: selected.value,
          idempotencyKey: this.idempotencyKey,
          customerContact: {
            email: String(formData.get('email') || '').trim(),
            phone: String(formData.get('phone') || '').trim(),
            firstName: String(formData.get('firstName') || '').trim(),
            lastName: String(formData.get('lastName') || '').trim(),
          },
          shippingAddress: this.getAddress(),
        };

        this.setLoading(this.createButton, true);
        try {
          const result = await this.postJson(this.dataset.createPath, payload);
          if (!result.shopifyPaymentUrl) {
            throw new Error('Shopify did not return a secure payment link.');
          }

          if (formData.get('rememberAddress') === 'true') {
            this.saveAddress();
          } else {
            this.clearSavedAddress();
          }

          this.saveRecovery(result.shopifyPaymentUrl);
          try {
            await this.clearCart();
          } catch (error) {
            console.warn('Checkout was created, but the cart could not be cleared.', error);
          }
          window.location.assign(result.shopifyPaymentUrl);
        } catch (error) {
          this.showMessage(error.message || 'Unable to prepare checkout. Please try again.');
          this.setLoading(this.createButton, false);
        }
      }

      getAddress() {
        const formData = new FormData(this.form);
        return {
          countryCode: String(formData.get('countryCode') || '').trim().toUpperCase(),
          provinceCode: String(formData.get('provinceCode') || '').trim().toUpperCase(),
          city: String(formData.get('city') || '').trim(),
          postalCode: String(formData.get('postalCode') || '').trim(),
          addressLine1: String(formData.get('addressLine1') || '').trim(),
          addressLine2: String(formData.get('addressLine2') || '').trim(),
        };
      }

      validateShippingAddress() {
        const fieldNames = ['addressLine1', 'countryCode', 'city', 'postalCode'];
        for (const name of fieldNames) {
          const field = this.form.elements.namedItem(name);
          if (!field?.reportValidity()) return false;
        }
        return true;
      }

      hasCompleteSavedAddress() {
        const formData = new FormData(this.form);
        return ['email', 'firstName', 'lastName', 'addressLine1', 'countryCode', 'city', 'postalCode'].every((name) =>
          String(formData.get(name) || '').trim()
        );
      }

      showDetailsSummary() {
        if (!this.hasCompleteSavedAddress()) return;

        const formData = new FormData(this.form);
        const countrySelect = this.form.elements.namedItem('countryCode');
        const countryName = countrySelect?.selectedOptions?.[0]?.textContent?.trim() || formData.get('countryCode');
        const locality = [
          String(formData.get('city') || '').trim(),
          String(formData.get('provinceCode') || '').trim().toUpperCase(),
          String(formData.get('postalCode') || '').trim(),
        ]
          .filter(Boolean)
          .join(', ');

        this.querySelector('[data-details-summary-name]').textContent = [
          String(formData.get('firstName') || '').trim(),
          String(formData.get('lastName') || '').trim(),
        ]
          .filter(Boolean)
          .join(' ');
        this.querySelector('[data-details-summary-contact]').textContent = [
          String(formData.get('email') || '').trim(),
          String(formData.get('phone') || '').trim(),
        ]
          .filter(Boolean)
          .join('\n');
        this.querySelector('[data-details-summary-address]').textContent = [
          String(formData.get('addressLine1') || '').trim(),
          String(formData.get('addressLine2') || '').trim(),
          locality,
          countryName,
        ]
          .filter(Boolean)
          .join('\n');

        this.detailsEditor.hidden = true;
        this.detailsSummary.hidden = false;
      }

      showDetailsEditor() {
        this.detailsSummary.hidden = true;
        this.detailsEditor.hidden = false;
      }

      setDetailsStatus(text) {
        this.detailsStatusLabel.textContent = text;
        this.detailsStatus.hidden = !text;
      }

      invalidateQuote() {
        this.quoteToken = null;
        this.idempotencyKey = null;
        this.shippingSection.hidden = true;
        this.shippingOptions.replaceChildren();
        this.querySelector('[data-shipping-total-row]').hidden = true;
      }

      updateSelectedShippingTotal() {
        const selected = this.form.querySelector('input[name="shippingOption"]:checked');
        const row = this.querySelector('[data-shipping-total-row]');
        if (!selected) {
          row.hidden = true;
          return;
        }

        this.querySelector('[data-shipping-total]').textContent = this.formatMoney(
          Number(selected.dataset.amount),
          selected.dataset.currency
        );
        row.hidden = false;
      }

      async postJson(url, body) {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        let payload;
        try {
          payload = await response.json();
        } catch (_error) {
          throw new Error('The checkout service returned an invalid response.');
        }

        if (!response.ok || (typeof payload.code !== 'undefined' && Number(payload.code) !== 0)) {
          throw new Error(payload.message || `Checkout request failed (${response.status}).`);
        }

        return payload.data || payload;
      }

      setLoading(button, loading) {
        button.disabled = loading;
        button.setAttribute('aria-busy', String(loading));
      }

      showMessage(text, type = 'error') {
        this.message.textContent = text;
        this.message.dataset.type = type;
        this.message.hidden = false;
        this.message.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
      }

      hideMessage() {
        this.message.hidden = true;
        this.message.textContent = '';
      }

      showEmptyState() {
        this.emptyState.hidden = false;
        const recovery = this.readRecovery();
        if (!recovery) return;

        const recoveryPanel = this.querySelector('[data-recovery]');
        const continueLink = this.querySelector('[data-continue-payment]');
        const restoreButton = this.querySelector('[data-restore-cart]');
        recoveryPanel.hidden = false;
        continueLink.href = recovery.shopifyPaymentUrl;
        restoreButton.addEventListener('click', () => this.restoreCart(recovery, restoreButton), { once: true });
      }

      saveAddress() {
        const formData = new FormData(this.form);
        const address = {
          email: String(formData.get('email') || ''),
          phone: String(formData.get('phone') || ''),
          firstName: String(formData.get('firstName') || ''),
          lastName: String(formData.get('lastName') || ''),
          ...this.getAddress(),
        };
        try {
          localStorage.setItem(this.addressStorageKey, JSON.stringify(address));
        } catch (_error) {
          // Saving an address is optional and should never block checkout.
        }
      }

      clearSavedAddress() {
        try {
          localStorage.removeItem(this.addressStorageKey);
        } catch (_error) {
          // Browser storage is optional and should never block checkout.
        }
      }

      isRememberAddressEnabled() {
        return Boolean(this.form.elements.namedItem('rememberAddress')?.checked);
      }

      restoreSavedAddress() {
        let saved;
        try {
          saved = JSON.parse(localStorage.getItem(this.addressStorageKey));
        } catch (_error) {
          return;
        }
        if (!saved) return;

        Object.entries(saved).forEach(([name, value]) => {
          const field = this.form.elements.namedItem(name);
          if (field && value) field.value = value;
        });
        this.form.elements.namedItem('rememberAddress').checked = true;
      }

      saveRecovery(shopifyPaymentUrl) {
        const recovery = {
          createdAt: Date.now(),
          shopifyPaymentUrl,
          items: this.cart.items.map((item) => ({
            id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties || {},
          })),
        };
        try {
          sessionStorage.setItem(this.recoveryStorageKey, JSON.stringify(recovery));
        } catch (_error) {
          // Recovery is best-effort; the secure payment redirect remains primary.
        }
      }

      readRecovery() {
        let recovery;
        try {
          recovery = JSON.parse(sessionStorage.getItem(this.recoveryStorageKey));
        } catch (_error) {
          return null;
        }

        if (!recovery || Date.now() - recovery.createdAt > 24 * 60 * 60 * 1000) {
          sessionStorage.removeItem(this.recoveryStorageKey);
          return null;
        }
        return recovery;
      }

      async restoreCart(recovery, button) {
        this.setLoading(button, true);
        try {
          const response = await fetch(`${this.dataset.rootUrl}cart/add.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ items: recovery.items }),
          });
          if (!response.ok) throw new Error('Unable to restore the cart.');

          sessionStorage.removeItem(this.recoveryStorageKey);
          window.location.assign(this.dataset.cartUrl);
        } catch (error) {
          this.showMessage(error.message);
          this.setLoading(button, false);
        }
      }

      async clearCart() {
        const response = await fetch(`${this.dataset.rootUrl}cart/clear.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error(`Cart clear request failed with status ${response.status}.`);
        }
      }

      formatMoney(amount, currency = this.currency) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency,
          }).format(Number(amount));
        } catch (_error) {
          return `${currency} ${Number(amount).toFixed(2)}`;
        }
      }

      resizeImage(url, size) {
        if (!url || url.includes('.svg')) return url;
        return url.replace(/(\.(?:jpe?g|png|webp))(\?.*)?$/i, `_${size}$1$2`);
      }

      createUuid() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
          const random = (Math.random() * 16) | 0;
          const value = character === 'x' ? random : (random & 0x3) | 0x8;
          return value.toString(16);
        });
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
    }
  );
}
